import { AlertCircle } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type { FieldIds } from './Field';
import styles from './SettingRow.module.css';

/**
 * One setting as a row (Job-salary-budget mockup, `.set`): its name and a one-line helper on the
 * left, the control on the right, a hairline between rows. On a phone the row stacks, name over
 * control. A wide row (a long text box) puts the control under the name at every width.
 *
 * Wired like `Field`: the render prop hands the control its id and what describes it, so the
 * name and any error are always announced with it. A row whose control is a group (a segmented
 * choice, two date boxes) names it with `labelId` instead.
 */

export interface SettingRowIds extends FieldIds {
  /** The name's id, for a group's `aria-labelledby`. */
  readonly labelId: string;
}

interface SettingRowProps {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string;
  readonly isRequired?: boolean;
  readonly isOptional?: boolean;
  /** The control under the name, full width (a text area). */
  readonly isWide?: boolean;
  /** The control is a group, not one input: the name is not a `<label>`. */
  readonly isGroup?: boolean;
  readonly children: (ids: SettingRowIds) => ReactNode;
}

export function SettingRow({
  label,
  hint,
  error,
  isRequired = false,
  isOptional = false,
  isWide = false,
  isGroup = false,
  children,
}: SettingRowProps): ReactNode {
  const inputId = useId();
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();
  const described = [hint === undefined ? '' : hintId, error === undefined ? '' : errorId].filter(Boolean).join(' ');
  const name = (
    <>
      {label}
      {isRequired && (
        <span className={styles.required} aria-label="required">
          *
        </span>
      )}
      {isOptional && <span className={styles.optional}>optional</span>}
    </>
  );

  return (
    <div className={styles.row} data-wide={isWide || undefined}>
      <div className={styles.text}>
        {isGroup ? (
          <span id={labelId} className={styles.label}>
            {name}
          </span>
        ) : (
          <label id={labelId} className={styles.label} htmlFor={inputId}>
            {name}
          </label>
        )}
        {hint !== undefined && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )}
      </div>
      <div className={styles.control} data-group={isGroup || undefined}>
        {children({ inputId, labelId, describedBy: described === '' ? undefined : described, isInvalid: error !== undefined })}
        {error !== undefined && (
          <span id={errorId} className={styles.error} role="alert">
            <AlertCircle size={13} aria-hidden />
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

/** A read-only setting: the name on the left, what it is on the right. */
export function SettingFact({ label, hint, children }: { readonly label: string; readonly hint?: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className={styles.row}>
      <div className={styles.text}>
        <span className={styles.label}>{label}</span>
        {hint !== undefined && <span className={styles.hint}>{hint}</span>}
      </div>
      <div className={`${styles.control} ${styles.fact}`}>{children}</div>
    </div>
  );
}
